import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TrendingUp, DollarSign, Store, AlertCircle, CheckCircle, 
  Play, RefreshCw, Bot, BarChart3, ArrowRight, ExternalLink,
  Zap, Target, Clock, ChevronRight, Activity, Globe, Users,
  ShoppingCart, Rocket, Eye, ThumbsUp, ThumbsDown, Settings,
  Database, Wifi, WifiOff, AlertTriangle, Search, Filter,
  ArrowUpRight, ArrowDownRight, Pause, XCircle, Timer, 
  TrendingDown, Percent, PieChart, Layers, MessageCircle, Phone,
  Bell, Send, MapPin, LogOut, Shield
} from "lucide-react";
import GeoTargetingModal from "../components/GeoTargetingModal";
import FinancialMetricsModal from "../components/FinancialMetricsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de métrica animada
const AnimatedMetric = ({ value, suffix = "", prefix = "", color = "emerald" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.round(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <span className={`text-${color}-400`}>
      {prefix}{typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}{suffix}
    </span>
  );
};

// Componente de estado del agente
const AgentStatusCard = ({ agent, index }) => {
  const isActive = agent.status === 'active';
  const isPending = agent.status === 'pending';
  
  return (
    <div 
      className={`p-4 rounded-xl border transition-all duration-300 ${
        isActive 
          ? 'bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50' 
          : isPending
          ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50'
          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
      }`}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-3 h-3 rounded-full ${
          isActive ? 'bg-emerald-400 animate-pulse' : 
          isPending ? 'bg-amber-400' : 'bg-zinc-600'
        }`} />
        <span className="font-mono text-sm font-semibold">{agent.name}</span>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">{agent.description}</p>
      <div className="mt-3 flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isActive ? 'bg-emerald-500/20 text-emerald-400' :
          isPending ? 'bg-amber-500/20 text-amber-400' :
          'bg-zinc-800 text-zinc-500'
        }`}>
          {isActive ? 'Activo' : isPending ? 'Pendiente' : 'Inactivo'}
        </span>
      </div>
    </div>
  );
};

// Componente de tarjeta de oportunidad
const OpportunityCard = ({ opp, onValidate, onApprove, onReject, onViewDetails }) => {
  const [showGeoModal, setShowGeoModal] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  
  const getStatusConfig = (status) => {
    const configs = {
      'detected': { 
        color: 'blue', 
        label: 'Detectado', 
        icon: Search,
        bg: 'bg-blue-500/10 border-blue-500/30'
      },
      'validated': { 
        color: 'emerald', 
        label: 'Validado', 
        icon: CheckCircle,
        bg: 'bg-emerald-500/10 border-emerald-500/30'
      },
      'approved': { 
        color: 'emerald', 
        label: 'Aprobado', 
        icon: ThumbsUp,
        bg: 'bg-emerald-500/10 border-emerald-500/30'
      },
      'pending_approval': { 
        color: 'amber', 
        label: 'Tu Revisión', 
        icon: AlertTriangle,
        bg: 'bg-amber-500/10 border-amber-500/30'
      },
      'live': { 
        color: 'purple', 
        label: 'En Vivo', 
        icon: Rocket,
        bg: 'bg-purple-500/10 border-purple-500/30'
      },
      'rejected': { 
        color: 'red', 
        label: 'Rechazado', 
        icon: XCircle,
        bg: 'bg-red-500/10 border-red-500/30'
      }
    };
    return configs[status] || configs['detected'];
  };

  const statusConfig = getStatusConfig(opp.status);
  const StatusIcon = statusConfig.icon;
  const score = opp.validation?.overall_score || 0;
  const roi = opp.financials?.projected_roi_percent || 0;
  
  // Calcular métricas de micro-comunidad
  const microCommunityScore = Math.round((opp.product?.trend_score || 0) * 0.4 + (opp.product?.viral_potential || 0) * 0.6);
  const marketSize = opp.product?.total_search_volume || opp.product?.search_volume || 0;
  const marketSizeFormatted = marketSize >= 1000000 
    ? `${(marketSize / 1000000).toFixed(1)}M` 
    : marketSize >= 1000 
    ? `${(marketSize / 1000).toFixed(0)}K` 
    : marketSize;
  const markets = opp.product?.markets || ['US'];
  const audienceReach = Math.round(marketSize * 0.15); // 15% del mercado como alcance potencial

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-all duration-300 group">
      <div className="flex flex-col gap-5">
        {/* Header: Producto + Status */}
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 ${statusConfig.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className={`w-7 h-7 text-${statusConfig.color}-400`} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1 group-hover:text-emerald-400 transition-colors">
              {opp.product?.name || 'Producto'}
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className={`text-xs px-3 py-1 rounded-full border ${statusConfig.bg} text-${statusConfig.color}-400 font-medium`}>
                {statusConfig.label}
              </span>
              {opp.product?.source_count && (
                <span className="text-xs px-3 py-1 rounded-full bg-zinc-800 text-zinc-400">
                  {opp.product.source_count} fuentes
                </span>
              )}
              {opp.product?.confidence_level && (
                <span className={`text-xs px-3 py-1 rounded-full ${
                  opp.product.confidence_level === 'HIGH' || opp.product.confidence_level === 'MAXIMUM' ? 'bg-emerald-500/20 text-emerald-400' :
                  opp.product.confidence_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-zinc-800 text-zinc-400'
                }`}>
                  {opp.product.confidence_level}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Métricas principales en grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-emerald-400">
              {Math.round(opp.product?.trend_score || 0)}
            </div>
            <div className="text-xs text-zinc-500">Trend Score</div>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-blue-400">
              +{Math.round(opp.product?.demand_growth_30d || opp.product?.demand_growth_7d || 0)}%
            </div>
            <div className="text-xs text-zinc-500">Crecimiento</div>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-400">
              {Math.round(opp.product?.viral_potential || 0)}
            </div>
            <div className="text-xs text-zinc-500">Potencial Viral</div>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-amber-400">
              {opp.product?.source_count || '-'}
            </div>
            <div className="text-xs text-zinc-500">Fuentes</div>
          </div>
        </div>

        {/* NUEVO: Panel de Micro-Comunidad y Área de Ataque */}
        <div className="bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-semibold text-violet-400">Análisis de Micro-Comunidad</span>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* Índice de Micro-Comunidad */}
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <div className="text-3xl font-bold text-violet-400">{microCommunityScore}</div>
                <div className="absolute -top-1 -right-3 text-xs text-violet-300">/100</div>
              </div>
              <div className="text-xs text-zinc-400 mt-1">Índice MC</div>
              <div className="text-[10px] text-zinc-500">Micro-Comunidad</div>
            </div>
            
            {/* Tamaño del Mercado */}
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">{marketSizeFormatted}</div>
              <div className="text-xs text-zinc-400 mt-1">Tamaño Mercado</div>
              <div className="text-[10px] text-zinc-500">Búsquedas/mes</div>
            </div>
            
            {/* Alcance Estimado */}
            <div className="text-center">
              <div className="text-2xl font-bold text-pink-400">
                {audienceReach >= 1000000 
                  ? `${(audienceReach / 1000000).toFixed(1)}M` 
                  : audienceReach >= 1000 
                  ? `${(audienceReach / 1000).toFixed(0)}K` 
                  : audienceReach}
              </div>
              <div className="text-xs text-zinc-400 mt-1">Alcance Est.</div>
              <div className="text-[10px] text-zinc-500">Audiencia Ads</div>
            </div>
            
            {/* Mercados de Ataque - CLICKEABLE */}
            <div 
              className="text-center cursor-pointer hover:bg-violet-500/20 rounded-lg p-2 -m-2 transition-all group/zone"
              onClick={() => setShowGeoModal(true)}
              data-testid={`geo-target-btn-${opp.id}`}
            >
              <div className="flex items-center justify-center gap-1 text-lg font-bold">
                {markets.slice(0, 3).map((market, idx) => (
                  <span key={idx} className={`px-1.5 py-0.5 rounded text-xs ${
                    market === 'US' ? 'bg-blue-500/20 text-blue-400' :
                    market === 'UK' ? 'bg-red-500/20 text-red-400' :
                    market === 'DE' ? 'bg-amber-500/20 text-amber-400' :
                    market === 'GLOBAL' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-zinc-700 text-zinc-300'
                  }`}>
                    {market}
                  </span>
                ))}
              </div>
              <div className="text-xs text-zinc-400 mt-1 flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3 group-hover/zone:text-violet-400 transition-colors" />
                <span className="group-hover/zone:text-violet-400 transition-colors">Zona Ataque</span>
              </div>
              <div className="text-[10px] text-zinc-500 group-hover/zone:text-violet-300 transition-colors">Click para ver mapa</div>
            </div>
          </div>
        </div>

        {/* Modal de Georeferenciación */}
        <GeoTargetingModal
          isOpen={showGeoModal}
          onClose={() => setShowGeoModal(false)}
          markets={markets}
          product={opp.product}
          microCommunityScore={microCommunityScore}
          marketSize={marketSize}
          audienceReach={audienceReach}
        />

        {/* NUEVO: Panel Financiero Resumido */}
        <div 
          className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-4 cursor-pointer hover:border-emerald-500/40 transition-all group/finance"
          onClick={() => setShowFinancialModal(true)}
          data-testid={`financial-panel-${opp.id}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-400">Panel Financiero</span>
            </div>
            <span className="text-[10px] text-zinc-500 group-hover/finance:text-emerald-300 transition-colors">Click para detalle</span>
          </div>
          
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <div className="text-lg font-bold text-red-400">${Math.round(marketSize * 0.001)}</div>
              <div className="text-[10px] text-zinc-500">Invertido</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-cyan-400">{Math.round(audienceReach * 1.5).toLocaleString()}</div>
              <div className="text-[10px] text-zinc-500">Impactados</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">{Math.round(audienceReach * 0.003)}</div>
              <div className="text-[10px] text-zinc-500">Ventas</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-amber-400">{((audienceReach * 0.003) / (audienceReach * 1.5) * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-zinc-500">Conversión</div>
            </div>
          </div>
        </div>

        {/* Modal Financiero */}
        <FinancialMetricsModal
          isOpen={showFinancialModal}
          onClose={() => setShowFinancialModal(false)}
          product={opp.product}
          marketSize={marketSize}
          audienceReach={audienceReach}
        />

        {/* Validación y Score */}
        <div className="flex flex-col sm:flex-row gap-4">
          {opp.validation && (
            <div className={`flex-1 rounded-xl p-4 ${
              score >= 80 ? 'bg-emerald-500/10 border border-emerald-500/30' :
              score >= 70 ? 'bg-amber-500/10 border border-amber-500/30' :
              'bg-red-500/10 border border-red-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-3xl font-bold ${
                    score >= 80 ? 'text-emerald-400' :
                    score >= 70 ? 'text-amber-400' :
                    'text-red-400'
                  }`}>
                    {Math.round(score)}
                  </div>
                  <div className="text-xs text-zinc-400">Score Total</div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    opp.validation.risk_level === 'LOW' 
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : opp.validation.risk_level === 'MEDIUM'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    Riesgo {opp.validation.risk_level}
                  </span>
                  {roi > 0 && (
                    <div className="mt-2">
                      <div className="text-lg font-bold text-emerald-400">
                        {Math.round(roi).toLocaleString()}%
                      </div>
                      <div className="text-xs text-zinc-500">ROI Proyectado</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Acciones */}
          <div className="flex flex-row sm:flex-col gap-2 sm:min-w-[160px]">
            {opp.status === 'detected' && (
              <button
                onClick={() => onValidate(opp.id)}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-all text-sm flex items-center justify-center gap-2"
                data-testid={`validate-btn-${opp.id}`}
              >
                <Zap className="w-4 h-4" />
                Validar
              </button>
            )}
            {(opp.status === 'validated' || opp.status === 'pending_approval') && (
              <>
                <button
                  onClick={() => onApprove(opp.id)}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-all text-sm flex items-center justify-center gap-2"
                  data-testid={`approve-btn-${opp.id}`}
                >
                  <ThumbsUp className="w-4 h-4" />
                  Aprobar
                </button>
                <button
                  onClick={() => onReject(opp.id)}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-zinc-700 transition-all text-sm flex items-center justify-center gap-2"
                  data-testid={`reject-btn-${opp.id}`}
                >
                  <ThumbsDown className="w-4 h-4" />
                  Rechazar
                </button>
              </>
            )}
            {opp.status === 'approved' && (
              <button
                onClick={() => onViewDetails(opp.id)}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-violet-500 text-white font-medium rounded-xl hover:bg-violet-400 transition-all text-sm flex items-center justify-center gap-2"
                data-testid={`launch-btn-${opp.id}`}
              >
                <Rocket className="w-4 h-4" />
                Lanzar
              </button>
            )}
            <button
              onClick={() => onViewDetails(opp.id)}
              className="flex-1 sm:flex-none px-4 py-2.5 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-zinc-700 transition-all text-sm flex items-center justify-center gap-2"
              data-testid={`details-btn-${opp.id}`}
            >
              <Eye className="w-4 h-4" />
              Detalles
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Principal
export default function CaucheraDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [hunting, setHunting] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [systemStatus, setSystemStatus] = useState({
    mongodb: 'checking',
    ai: 'checking',
    agents: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [lastScan, setLastScan] = useState(null);
  
  // WhatsApp config
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Intelligence Coverage
  const [intelligenceCoverage, setIntelligenceCoverage] = useState(null);
  const [showIntelligenceModal, setShowIntelligenceModal] = useState(false);
  
  // CEO Financial Dashboard
  const [ceoData, setCeoData] = useState(null);
  const [showCeoModal, setShowCeoModal] = useState(false);

  // Cargar datos del dashboard
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar en paralelo con cache-busting para datos críticos
      const timestamp = Date.now();
      const [oppRes, agentsRes, healthRes, sourcesRes, intelligenceRes, ceoRes] = await Promise.all([
        fetch(`${API_URL}/api/cauchera/opportunities/?limit=50`),
        fetch(`${API_URL}/api/cauchera/dashboard/agents/status`),
        fetch(`${API_URL}/api/health`),
        fetch(`${API_URL}/api/cauchera/config/data-sources`).catch(() => null),
        fetch(`${API_URL}/api/cauchera/config/intelligence?_t=${timestamp}`).catch(() => null),
        fetch(`${API_URL}/api/cauchera/config/ceo/summary`).catch(() => null)
      ]);

      const oppData = await oppRes.json();
      if (oppData.success) {
        setOpportunities(oppData.data.opportunities);
      }

      const agentsData = await agentsRes.json();
      if (agentsData.success) {
        setAgents(agentsData.data.agents);
      }

      const healthData = await healthRes.json();
      setSystemStatus({
        mongodb: healthData.mongodb === 'connected' ? 'connected' : 'error',
        ai: healthData.ai_configured ? 'configured' : 'missing',
        agents: healthData.agents_available || 0
      });

      if (sourcesRes) {
        const sourcesData = await sourcesRes.json();
        if (sourcesData.success) {
          setDataSources(sourcesData.data.sources || []);
        }
      }
      
      if (intelligenceRes) {
        const intelligenceData = await intelligenceRes.json();
        if (intelligenceData.success) {
          setIntelligenceCoverage(intelligenceData.data);
        }
      }
      
      if (ceoRes) {
        const ceoDataRes = await ceoRes.json();
        if (ceoDataRes.success) {
          setCeoData(ceoDataRes.data);
        }
      }

    } catch (error) {
      console.error("Error loading dashboard:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadWhatsAppStatus();
    // Auto-refresh cada 30 segundos
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  // Cargar estado de WhatsApp
  const loadWhatsAppStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cauchera/config/whatsapp/status`);
      const data = await res.json();
      if (data.success) {
        setWhatsappStatus(data.data);
        if (data.data.phone_configured) {
          setWhatsappPhone(data.data.phone_configured);
        }
      }
    } catch (error) {
      console.error("Error loading WhatsApp status:", error);
    }
  };

  // Configurar WhatsApp
  const configureWhatsApp = async () => {
    if (!whatsappPhone) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cauchera/config/whatsapp/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: whatsappPhone })
      });
      const data = await res.json();
      if (data.success) {
        await loadWhatsAppStatus();
        alert('WhatsApp configurado correctamente');
      }
    } catch (error) {
      console.error("Error configuring WhatsApp:", error);
    }
  };

  // Enviar mensaje de prueba
  const sendTestMessage = async () => {
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/cauchera/config/whatsapp/test`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        alert('Mensaje de prueba enviado. Revisa tu WhatsApp!');
      } else {
        alert('Error: ' + (data.message || 'No se pudo enviar'));
      }
    } catch (error) {
      console.error("Error sending test:", error);
      alert('Error enviando mensaje de prueba');
    }
    setSendingTest(false);
  };

  // Ejecutar ProductHunter (Multi-fuente)
  const runProductHunt = async () => {
    setHunting(true);
    try {
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/hunt`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setLastScan(new Date());
        await loadDashboardData();
      }
    } catch (error) {
      console.error("Error hunting products:", error);
    }
    setHunting(false);
  };

  // Ejecutar TrendScanner (Legacy)
  const runTrendScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/scan`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setLastScan(new Date());
        await loadDashboardData();
      }
    } catch (error) {
      console.error("Error scanning trends:", error);
    }
    setScanning(false);
  };

  // Validar oportunidad
  const validateOpportunity = async (id) => {
    try {
      // Primero identificar comunidades
      await fetch(`${API_URL}/api/cauchera/opportunities/${id}/target-communities`, {
        method: 'POST'
      });
      
      // Luego validar
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/${id}/validate`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        await loadDashboardData();
      }
    } catch (error) {
      console.error("Error validating:", error);
    }
  };

  // Aprobar oportunidad
  const approveOpportunity = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/${id}/approve`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        await loadDashboardData();
      }
    } catch (error) {
      console.error("Error approving:", error);
    }
  };

  // Rechazar oportunidad
  const rejectOpportunity = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/cauchera/opportunities/${id}/reject`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        await loadDashboardData();
      }
    } catch (error) {
      console.error("Error rejecting:", error);
    }
  };

  // Métricas calculadas
  const metrics = {
    total: opportunities.length,
    detected: opportunities.filter(o => o.status === 'detected').length,
    pending: opportunities.filter(o => o.status === 'pending_approval').length,
    approved: opportunities.filter(o => o.status === 'approved').length,
    live: opportunities.filter(o => o.status === 'live').length,
    rejected: opportunities.filter(o => o.status === 'rejected').length,
    avgScore: opportunities.filter(o => o.validation).length > 0 
      ? Math.round(opportunities.filter(o => o.validation).reduce((acc, o) => acc + (o.validation?.overall_score || 0), 0) / opportunities.filter(o => o.validation).length)
      : 0,
    avgROI: opportunities.filter(o => o.financials).length > 0
      ? Math.round(opportunities.filter(o => o.financials).reduce((acc, o) => acc + (o.financials?.projected_roi_percent || 0), 0) / opportunities.filter(o => o.financials).length)
      : 0,
    activeAgents: agents.filter(a => a.status === 'active').length,
    totalAgents: agents.length || 12
  };

  // Filtrar oportunidades
  const filteredOpportunities = filterStatus === 'all' 
    ? opportunities 
    : opportunities.filter(o => o.status === filterStatus);

  if (loading && opportunities.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-emerald-500/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-emerald-400 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-zinc-400 text-lg">Inicializando CAUCHERA...</p>
          <p className="text-zinc-600 text-sm mt-2">Conectando con agentes de IA</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="font-bold text-xl">CAUCHERA</h1>
                  <p className="text-xs text-zinc-500">Centro de Operaciones</p>
                </div>
              </div>
              
              {/* Status indicators */}
              <div className="hidden md:flex items-center gap-3 ml-6 pl-6 border-l border-zinc-800">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    systemStatus.mongodb === 'connected' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'
                  }`} />
                  <span className="text-xs text-zinc-500">DB</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    systemStatus.ai === 'configured' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
                  }`} />
                  <span className="text-xs text-zinc-500">IA</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-500">{metrics.activeAgents}/{metrics.totalAgents}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {lastScan && (
                <span className="text-xs text-zinc-500 hidden lg:block">
                  Último scan: {lastScan.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={runProductHunt}
                disabled={hunting || scanning}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-black font-semibold rounded-xl hover:from-emerald-400 hover:to-emerald-500 transition-all disabled:opacity-50 shadow-lg shadow-emerald-500/20"
              >
                {hunting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Target className="w-4 h-4" />
                )}
                {hunting ? 'Buscando...' : 'Detectar Productos'}
              </button>
              
              {/* Botón Cerrar Sesión */}
              <button
                onClick={() => {
                  if (onLogout) {
                    onLogout();
                    navigate('/login');
                  }
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800 text-zinc-300 font-medium rounded-xl hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 border border-zinc-700 transition-all"
                data-testid="logout-button"
                title="Cerrar Sesión"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Métricas Principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <Target className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold mb-1">{metrics.total}</div>
            <div className="text-xs text-zinc-500">Total</div>
          </div>
          
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <Search className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-cyan-400 mb-1">{metrics.detected}</div>
            <div className="text-xs text-zinc-500">Detectados</div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/5 to-zinc-900/50 border border-amber-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="text-3xl font-bold text-amber-400 mb-1">{metrics.pending}</div>
            <div className="text-xs text-zinc-500">Tu Revisión</div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500/5 to-zinc-900/50 border border-emerald-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-3xl font-bold text-emerald-400 mb-1">{metrics.approved}</div>
            <div className="text-xs text-zinc-500">Aprobados</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/5 to-zinc-900/50 border border-purple-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <Rocket className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-bold text-purple-400 mb-1">{metrics.live}</div>
            <div className="text-xs text-zinc-500">En Vivo</div>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <Bot className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-3xl font-bold mb-1">
              <span className="text-emerald-400">{metrics.activeAgents}</span>
              <span className="text-zinc-600">/{metrics.totalAgents}</span>
            </div>
            <div className="text-xs text-zinc-500">Agentes</div>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-blue-400 mb-1">{metrics.avgScore}</div>
            <div className="text-xs text-zinc-500">Score Prom.</div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/5 to-zinc-900/50 border border-emerald-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-3xl font-bold text-emerald-400 mb-1">{metrics.avgROI}%</div>
            <div className="text-xs text-zinc-500">ROI Prom.</div>
          </div>
        </div>

        {/* Grid de Agentes */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bot className="w-5 h-5 text-emerald-400" />
              Dream Team de Agentes
            </h2>
            <span className="text-sm text-zinc-500">
              {metrics.activeAgents} activos de {metrics.totalAgents}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {agents.map((agent, i) => (
              <AgentStatusCard key={i} agent={agent} index={i} />
            ))}
          </div>
        </div>

        {/* Reglas de Aprobación */}
        <div className="bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-8">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-zinc-400" />
            Reglas de Aprobación Automática
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
                <span className="font-bold text-emerald-400">Score ≥ 80%</span>
              </div>
              <p className="text-sm text-zinc-400">
                AUTO-APROBADO - Ejecución automática sin intervención
              </p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <span className="font-bold text-amber-400">Score 70-80%</span>
              </div>
              <p className="text-sm text-zinc-400">
                TU REVISIÓN - Aparece en el dashboard para tu decisión
              </p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <XCircle className="w-6 h-6 text-red-400" />
                <span className="font-bold text-red-400">Score &lt; 70%</span>
              </div>
              <p className="text-sm text-zinc-400">
                RECHAZADO - No cumple umbral mínimo de rentabilidad
              </p>
            </div>
          </div>
        </div>

        {/* 💰 WIDGET CEO FINANCIAL DASHBOARD */}
        {ceoData && (
          <div className="bg-gradient-to-r from-emerald-500/10 via-zinc-900 to-zinc-900 border border-emerald-500/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
              {/* Métricas Principales */}
              <div className="flex-1">
                <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Vista CEO - Finanzas
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    ceoData.financial_overview?.health_indicators?.overall_status === 'excellent'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : ceoData.financial_overview?.health_indicators?.overall_status === 'good'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : ceoData.financial_overview?.health_indicators?.overall_status === 'warning'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {ceoData.financial_overview?.health_indicators?.overall_score || 0}/100
                  </span>
                </h3>
                
                {/* Grid de métricas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="text-2xl font-bold text-emerald-400">
                      ${ceoData.financial_overview?.summary?.total_revenue?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-zinc-500">Revenue (30d)</div>
                    <div className={`text-xs mt-1 flex items-center gap-1 ${
                      ceoData.financial_overview?.trends?.direction === 'up' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {ceoData.financial_overview?.trends?.direction === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {ceoData.financial_overview?.trends?.revenue_growth || 0}%
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="text-2xl font-bold text-blue-400">
                      ${ceoData.financial_overview?.summary?.net_profit?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-zinc-500">Beneficio Neto</div>
                    <div className="text-xs mt-1 text-zinc-400">
                      {ceoData.financial_overview?.summary?.profit_margin || 0}% margen
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="text-2xl font-bold text-violet-400">
                      {ceoData.financial_overview?.summary?.roas || 0}x
                    </div>
                    <div className="text-xs text-zinc-500">ROAS</div>
                    <div className={`text-xs mt-1 ${
                      (ceoData.financial_overview?.summary?.roas || 0) >= 2 ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {(ceoData.financial_overview?.summary?.roas || 0) >= 2 ? 'Rentable' : 'Optimizar'}
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="text-2xl font-bold text-amber-400">
                      {ceoData.financial_overview?.performance?.total_orders || 0}
                    </div>
                    <div className="text-xs text-zinc-500">Órdenes (30d)</div>
                    <div className="text-xs mt-1 text-zinc-400">
                      ${ceoData.financial_overview?.performance?.aov?.toFixed(0) || 0} AOV
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Mercados */}
              <div className="w-full lg:w-64">
                <h4 className="text-sm font-medium text-zinc-400 mb-3">Top Mercados</h4>
                <div className="space-y-2">
                  {ceoData.markets_performance?.markets?.slice(0, 3).map((market, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{
                          market.market === 'US' ? '🇺🇸' :
                          market.market === 'UK' ? '🇬🇧' :
                          market.market === 'DE' ? '🇩🇪' :
                          market.market === 'CA' ? '🇨🇦' :
                          market.market === 'AU' ? '🇦🇺' :
                          market.market === 'MX' ? '🇲🇽' :
                          market.market === 'ES' ? '🇪🇸' : '🌍'
                        }</span>
                        <span className="text-sm">{market.market}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">${market.revenue?.toLocaleString()}</div>
                        <div className={`text-xs ${market.roas >= 2 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {market.roas}x
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Botón Ver Más */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setShowCeoModal(true)}
                  className="px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-400 transition-all flex items-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  Ver Dashboard Completo
                </button>
              </div>
            </div>

            {/* Alertas si hay */}
            {ceoData.alerts && ceoData.alerts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex flex-wrap gap-2">
                  {ceoData.alerts.slice(0, 2).map((alert, idx) => (
                    <div key={idx} className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 ${
                      alert.type === 'critical' ? 'bg-red-500/20 text-red-400' :
                      alert.type === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {alert.type === 'critical' ? <AlertCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {alert.title}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal CEO Dashboard Completo */}
        {showCeoModal && ceoData && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-emerald-400" />
                    Dashboard Financiero CEO
                  </h2>
                  <p className="text-sm text-zinc-400">{ceoData.period}</p>
                </div>
                <button
                  onClick={() => setShowCeoModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              {/* Métricas principales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 rounded-xl p-4">
                  <div className="text-3xl font-bold text-emerald-400">
                    ${ceoData.financial_overview?.summary?.total_revenue?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-zinc-400">Revenue Total</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/30 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-400">
                    ${ceoData.financial_overview?.summary?.net_profit?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-zinc-400">Beneficio Neto</div>
                </div>
                <div className="bg-gradient-to-br from-violet-500/20 to-violet-500/5 border border-violet-500/30 rounded-xl p-4">
                  <div className="text-3xl font-bold text-violet-400">
                    {ceoData.financial_overview?.summary?.roas || 0}x
                  </div>
                  <div className="text-sm text-zinc-400">ROAS</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-4">
                  <div className="text-3xl font-bold text-amber-400">
                    {ceoData.financial_overview?.summary?.profit_margin || 0}%
                  </div>
                  <div className="text-sm text-zinc-400">Margen</div>
                </div>
              </div>

              {/* Desglose de costos */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h3 className="font-semibold mb-4">Desglose de Costos</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Ad Spend</span>
                      <span className="font-medium">${ceoData.financial_overview?.breakdown?.ad_spend?.toLocaleString() || 0} ({ceoData.financial_overview?.breakdown?.ad_spend_percent || 0}%)</span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div className="bg-violet-500 h-2 rounded-full" style={{width: `${ceoData.financial_overview?.breakdown?.ad_spend_percent || 0}%`}} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">COGS</span>
                      <span className="font-medium">${ceoData.financial_overview?.breakdown?.cogs?.toLocaleString() || 0} ({ceoData.financial_overview?.breakdown?.cogs_percent || 0}%)</span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div className="bg-amber-500 h-2 rounded-full" style={{width: `${ceoData.financial_overview?.breakdown?.cogs_percent || 0}%`}} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Fees</span>
                      <span className="font-medium">${ceoData.financial_overview?.breakdown?.fees?.toLocaleString() || 0} ({ceoData.financial_overview?.breakdown?.fees_percent || 0}%)</span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{width: `${ceoData.financial_overview?.breakdown?.fees_percent || 0}%`}} />
                    </div>
                  </div>
                </div>

                {/* Proyecciones */}
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h3 className="font-semibold mb-4">Proyecciones (30 días)</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">
                        ${ceoData.projections_30d?.projected_revenue?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-zinc-400">Revenue Proyectado</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">
                        ${ceoData.projections_30d?.projected_profit?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-zinc-400">Beneficio Proyectado</div>
                    </div>
                    <div className="text-sm text-zinc-500">
                      Crecimiento semanal: {ceoData.projections_30d?.growth_rate_weekly || 0}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance por mercado */}
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold mb-4">Performance por Mercado</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {ceoData.markets_performance?.markets?.map((market, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      market.status === 'profitable' ? 'bg-emerald-500/10 border-emerald-500/30' :
                      market.status === 'marginal' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{
                          market.market === 'US' ? '🇺🇸' :
                          market.market === 'UK' ? '🇬🇧' :
                          market.market === 'DE' ? '🇩🇪' :
                          market.market === 'CA' ? '🇨🇦' :
                          market.market === 'AU' ? '🇦🇺' :
                          market.market === 'MX' ? '🇲🇽' :
                          market.market === 'ES' ? '🇪🇸' : '🌍'
                        }</span>
                        <span className="font-medium">{market.market}</span>
                      </div>
                      <div className="text-lg font-bold">${market.revenue?.toLocaleString()}</div>
                      <div className="text-xs text-zinc-400">{market.orders} órdenes • {market.roas}x ROAS</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top productos */}
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold mb-4">Top Productos</h3>
                <div className="space-y-2">
                  {ceoData.top_products?.products?.map((product, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-zinc-600">#{idx + 1}</span>
                        <div>
                          <div className="font-medium">{product.name}</div>
                          <div className="text-xs text-zinc-500">{product.units} unidades vendidas</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${product.revenue?.toLocaleString()}</div>
                        <div className={`text-xs ${product.trend === 'up' ? 'text-emerald-400' : 'text-zinc-400'}`}>
                          {product.margin}% margen • {product.trend === 'up' ? '↑' : '→'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recomendaciones */}
              {ceoData.recommendations && ceoData.recommendations.length > 0 && (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h3 className="font-semibold mb-4">Recomendaciones Estratégicas</h3>
                  <div className="space-y-3">
                    {ceoData.recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        rec.priority === 'high' ? 'bg-emerald-500/10 border-emerald-500/30' :
                        rec.priority === 'medium' ? 'bg-amber-500/10 border-amber-500/30' :
                        'bg-zinc-700/50 border-zinc-700'
                      }`}>
                        <div className="font-medium">{rec.title}</div>
                        <div className="text-sm text-zinc-400 mt-1">{rec.description}</div>
                        <div className="text-xs text-zinc-500 mt-2">
                          <span className="text-emerald-400">{rec.expected_impact}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowCeoModal(false)}
                  className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🧠 WIDGET DE COBERTURA DE INTELIGENCIA - CEO View */}
        {intelligenceCoverage && (
          <div className="bg-gradient-to-r from-violet-500/10 via-zinc-900 to-zinc-900 border border-violet-500/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Indicador Principal */}
              <div className="flex items-center gap-4">
                <div className="relative w-24 h-24">
                  {/* Círculo de progreso */}
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50" cy="50" r="40"
                      className="fill-none stroke-zinc-800 stroke-[8]"
                    />
                    <circle
                      cx="50" cy="50" r="40"
                      className={`fill-none stroke-[8] transition-all duration-1000 ${
                        intelligenceCoverage.coverage.intelligence_coverage_index >= 70 
                          ? 'stroke-emerald-500' 
                          : intelligenceCoverage.coverage.intelligence_coverage_index >= 50 
                          ? 'stroke-amber-500' 
                          : 'stroke-red-500'
                      }`}
                      strokeLinecap="round"
                      strokeDasharray={`${intelligenceCoverage.coverage.intelligence_coverage_index * 2.51} 251`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className={`text-2xl font-bold ${
                      intelligenceCoverage.coverage.intelligence_coverage_index >= 70 
                        ? 'text-emerald-400' 
                        : intelligenceCoverage.coverage.intelligence_coverage_index >= 50 
                        ? 'text-amber-400' 
                        : 'text-red-400'
                    }`}>
                      {Math.round(intelligenceCoverage.coverage.intelligence_coverage_index)}%
                    </span>
                    <span className="text-xs text-zinc-500">ICI</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    Cobertura de Inteligencia
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      intelligenceCoverage.coverage.interpretation.level === 'MÁXIMO'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : intelligenceCoverage.coverage.interpretation.level === 'ALTO'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : intelligenceCoverage.coverage.interpretation.level === 'MEDIO'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {intelligenceCoverage.coverage.interpretation.level}
                    </span>
                  </h3>
                  <p className="text-sm text-zinc-400 max-w-md">
                    {intelligenceCoverage.coverage.interpretation.description}
                  </p>
                  <p className="text-xs text-violet-400 mt-1">
                    {intelligenceCoverage.coverage.interpretation.action}
                  </p>
                </div>
              </div>

              {/* Métricas Rápidas */}
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">
                    {intelligenceCoverage.coverage.metrics.sources_active}
                  </div>
                  <div className="text-xs text-zinc-500">Fuentes Activas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400">
                    {intelligenceCoverage.coverage.metrics.sources_available}
                  </div>
                  <div className="text-xs text-zinc-500">Disponibles</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-violet-400">
                    {intelligenceCoverage.coverage.metrics.premium_upgrades_available}
                  </div>
                  <div className="text-xs text-zinc-500">Premium</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-zinc-400">
                    {Math.round(intelligenceCoverage.coverage.quality.average_data_quality)}%
                  </div>
                  <div className="text-xs text-zinc-500">Calidad Data</div>
                </div>
              </div>

              {/* Botón de Ver Más */}
              <button
                onClick={() => setShowIntelligenceModal(true)}
                className="px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-400 transition-all flex items-center gap-2"
              >
                <Database className="w-4 h-4" />
                Ver Fuentes & Mejoras
              </button>
            </div>

            {/* Barra de Cobertura por Función */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(intelligenceCoverage.coverage.coverage_by_function).map(([func, data]) => (
                <div key={func} className="bg-zinc-800/50 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-zinc-400 capitalize">
                      {func.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-xs font-bold ${
                      data.status === 'optimal' ? 'text-emerald-400' :
                      data.status === 'adequate' ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {Math.round(data.coverage_percent)}%
                    </span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        data.status === 'optimal' ? 'bg-emerald-500' :
                        data.status === 'adequate' ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${data.coverage_percent}%` }}
                    />
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {data.active_sources}/{data.total_available} fuentes
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modal de Inteligencia */}
        {showIntelligenceModal && intelligenceCoverage && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold">Centro de Inteligencia de Datos</h2>
                  <p className="text-sm text-zinc-400">Gestiona tus fuentes de datos para máxima certeza analítica</p>
                </div>
                <button
                  onClick={() => setShowIntelligenceModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              {/* Indicador Grande */}
              <div className="bg-gradient-to-r from-violet-500/10 to-zinc-800 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-4xl font-bold text-violet-400">
                      {Math.round(intelligenceCoverage.coverage.intelligence_coverage_index)}%
                    </div>
                    <div className="text-sm text-zinc-400">Cobertura Actual</div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-zinc-600" />
                  <div>
                    <div className="text-4xl font-bold text-emerald-400">
                      {intelligenceCoverage.coverage.recommendations?.potential_ici || 100}%
                    </div>
                    <div className="text-sm text-zinc-400">Potencial Máximo</div>
                  </div>
                </div>
              </div>

              {/* Recomendaciones */}
              <div className="mb-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Recomendaciones para Mejorar
                </h3>
                <div className="space-y-3">
                  {intelligenceCoverage.coverage.recommendations?.recommendations?.slice(0, 5).map((rec, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-xl border ${
                        rec.type === 'FREE_UPGRADE' 
                          ? 'bg-emerald-500/5 border-emerald-500/30' 
                          : 'bg-amber-500/5 border-amber-500/30'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            {rec.name}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              rec.type === 'FREE_UPGRADE'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {rec.cost}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">{rec.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-violet-400">{rec.impact_on_ici}</div>
                          <div className="text-xs text-zinc-500">impacto en ICI</div>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between items-center">
                        <span className={`text-xs ${
                          rec.priority === 'HIGH' ? 'text-red-400' :
                          rec.priority === 'MEDIUM' ? 'text-amber-400' : 'text-zinc-500'
                        }`}>
                          Prioridad: {rec.priority}
                        </span>
                        {rec.setup_url && (
                          <a
                            href={rec.setup_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                          >
                            Configurar <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lista de Fuentes */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  Todas las Fuentes de Datos
                </h3>
                <div className="grid gap-3">
                  {intelligenceCoverage.sources?.map((source, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-xl border flex justify-between items-center ${
                        source.status === 'active' 
                          ? 'bg-emerald-500/5 border-emerald-500/30' 
                          : source.status === 'configured'
                          ? 'bg-blue-500/5 border-blue-500/30'
                          : 'bg-zinc-800/50 border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          source.status === 'active' ? 'bg-emerald-400 animate-pulse' :
                          source.status === 'configured' ? 'bg-blue-400' : 'bg-zinc-600'
                        }`} />
                        <div>
                          <div className="font-medium">{source.name}</div>
                          <div className="text-xs text-zinc-500">
                            Peso: {source.metrics.intelligence_weight}% | 
                            Calidad: {source.metrics.data_quality}% | 
                            {source.tier.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {source.cost.monthly_usd > 0 && (
                          <span className="text-xs text-amber-400">
                            ${source.cost.monthly_usd}/mes
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          source.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                          source.status === 'configured' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {source.status === 'active' ? 'Activo' :
                           source.status === 'configured' ? 'Configurado' : 'Disponible'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowIntelligenceModal(false)}
                  className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Widget de Notificaciones WhatsApp */}
        <div className="bg-gradient-to-r from-green-500/5 via-zinc-900 to-zinc-900 border border-green-500/20 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  Notificaciones WhatsApp
                  {whatsappStatus?.notifications_enabled && (
                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                      Activo
                    </span>
                  )}
                </h3>
                <p className="text-sm text-zinc-400">
                  {whatsappStatus?.phone_configured 
                    ? `Configurado: ${whatsappStatus.phone_configured}`
                    : 'Recibe alertas de oportunidades en tu celular'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowWhatsAppModal(true)}
              className="px-5 py-2.5 bg-green-500 text-black font-semibold rounded-xl hover:bg-green-400 transition-all flex items-center gap-2"
            >
              <Bell className="w-4 h-4" />
              {whatsappStatus?.phone_configured ? 'Cambiar Número' : 'Configurar'}
            </button>
          </div>
        </div>

        {/* Modal de WhatsApp */}
        {showWhatsAppModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Configurar WhatsApp</h3>
                  <p className="text-sm text-zinc-400">Recibe notificaciones instantáneas</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    Número de WhatsApp (con código de país)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={whatsappPhone}
                      onChange={(e) => setWhatsappPhone(e.target.value)}
                      placeholder="+573001234567"
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Ejemplos: +57 (Colombia), +52 (México), +34 (España), +1 (USA)
                  </p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <p className="text-sm text-zinc-400 mb-2">Recibirás notificaciones cuando:</p>
                  <ul className="text-sm text-zinc-500 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Se detecte una oportunidad de alto score
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      Una oportunidad requiera tu revisión
                    </li>
                    <li className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-400" />
                      Resumen diario de operaciones
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="flex-1 px-4 py-3 border border-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-800 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    await configureWhatsApp();
                    setShowWhatsAppModal(false);
                  }}
                  disabled={!whatsappPhone}
                  className="flex-1 px-4 py-3 bg-green-500 text-black font-semibold rounded-xl hover:bg-green-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Guardar
                </button>
              </div>
              
              {whatsappStatus?.phone_configured && (
                <button
                  onClick={sendTestMessage}
                  disabled={sendingTest}
                  className="w-full mt-3 px-4 py-2 border border-green-500/30 text-green-400 rounded-xl hover:bg-green-500/10 transition-all text-sm flex items-center justify-center gap-2"
                >
                  {sendingTest ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageCircle className="w-4 h-4" />
                  )}
                  Enviar Mensaje de Prueba
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filtros y Lista de Oportunidades */}
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              Oportunidades Detectadas
            </h2>
            
            <div className="flex items-center gap-3">
              {/* Filtros */}
              <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
                {[
                  { value: 'all', label: 'Todas' },
                  { value: 'detected', label: 'Nuevas' },
                  { value: 'pending_approval', label: 'Revisar' },
                  { value: 'approved', label: 'Aprobadas' },
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => setFilterStatus(filter.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      filterStatus === filter.value 
                        ? 'bg-emerald-500 text-black' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              
              <button
                onClick={loadDashboardData}
                className="p-2.5 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {filteredOpportunities.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-16 text-center">
              <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-10 h-10 text-zinc-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {filterStatus === 'all' ? 'No hay oportunidades' : `No hay oportunidades ${filterStatus}`}
              </h3>
              <p className="text-zinc-500 mb-8 max-w-md mx-auto">
                Ejecuta el detector de productos multi-fuente para encontrar oportunidades con demanda confirmada
              </p>
              <button
                onClick={runProductHunt}
                disabled={hunting}
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-black font-semibold rounded-xl hover:from-emerald-400 hover:to-emerald-500 transition-all shadow-lg shadow-emerald-500/20"
              >
                <div className="flex items-center gap-2">
                  {hunting ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Target className="w-5 h-5" />
                  )}
                  Detectar Productos Multi-Fuente
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOpportunities.map((opp) => (
                <OpportunityCard 
                  key={opp.id}
                  opp={opp}
                  onValidate={validateOpportunity}
                  onApprove={approveOpportunity}
                  onReject={rejectOpportunity}
                  onViewDetails={(id) => navigate(`/cauchera/opportunity/${id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer Status Bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-zinc-950/90 backdrop-blur-xl border-t border-zinc-900 py-3 px-6">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Database className="w-3 h-3" />
              MongoDB: {systemStatus.mongodb === 'connected' ? 'Conectado' : 'Error'}
            </span>
            <span className="flex items-center gap-2">
              <Bot className="w-3 h-3" />
              IA GPT-4o: {systemStatus.ai === 'configured' ? 'Activo' : 'Pendiente'}
            </span>
            <span className="flex items-center gap-2">
              <Layers className="w-3 h-3" />
              {agents.filter(a => a.status === 'active').length} agentes activos
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span>CAUCHERA v1.0</span>
            <span>•</span>
            <span>100% Automatizado</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
